import { ExportAssistantEvaluationEntriesCsvType } from '../types/AssistantsServiceTypes';
import AssistantsService from '../services/AssistantsService';
import { useEffect, useMemo } from 'react';
import { useAuth } from '../../../../../../contexts/auth/AuthContext';
import { useQuery, useMutation } from '@tanstack/react-query';
import { AdminQueryKeys } from '../../../../../../shared/react-query/enums/AdminQueryKeys';
import { AssistantsEvaluationCollectionResponse, AssistantsEvaluationsResponse, AssistantsResponse, CreateAssistantEvaluationResponse, ManualEntry, PublishLiveAssistantVersionType, WithEvaluationRun, WithRevision, WithVersion } from '../types/AssistantsServiceTypes';
import { queryClientInstance } from '../../../../../../shared/react-query';
import { RoutingPathsType } from '../../../helpers/routing';
import { ResponseBaseData } from '../../../types/AdminServiceTypes';
import { useBackendEvents } from '../../../../../../contexts/backend-events/BackendEventsContext';
import { AssistantEvent } from '../../../../../../models/BackendEvents';

let assistantsServiceInstance: AssistantsService | null = null;

// TODO: This must be turned into a context similar to ChatServiceProvider as the assistantsServiceInstance singleton is problematic
export const useAssistantsService = () => {
    const { getAccessToken } = useAuth();
    const backendEvents = useBackendEvents();
    const service = useMemo(() => {
        if (!assistantsServiceInstance) {
            assistantsServiceInstance = new AssistantsService(getAccessToken, backendEvents);
            assistantsServiceInstance.bindEvents();
        }
        return assistantsServiceInstance;
    }, [getAccessToken, backendEvents]);

    return { service };
};

const useAssistantsEvents = () => {
    const { service: { events } } = useAssistantsService();

    return events.assistant;
};

export const useListenToAssistantsEvents = (assistantId: string, refetch: <T extends Object>(_: T) => void) => {
    const assistantEvents = useAssistantsEvents();

    useEffect(() => {
        const pathPrefix = `evaluation.runs.${assistantId}`;
        const handler = (event: AssistantEvent) => {
            if (!event.path.startsWith(pathPrefix))
                return;
            refetch({});
        };
        assistantEvents.on(handler);
        return () => {
            assistantEvents.off(handler);
        };
    }, [assistantId, assistantEvents, refetch]);
};


const useGetAssistants = <TSelect extends AssistantsResponse, TResult = TSelect>({ select }: { select: (props: AssistantsResponse) => TResult; }) => {
    const { service } = useAssistantsService();
    return useQuery({
        queryKey: AdminQueryKeys.assistants(),
        queryFn: service.getAssistants,
        select,
        staleTime: 0
    });
};

export const useGetAssistantsPermissions = () => {
    return useGetAssistants({
        select: ({ permissions }) => permissions,
    });
};

export const useGetAssistantsData = () => {
    return useGetAssistants({
        select: ({ data }) => data
    });
};

const refreshAssistants = () => queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistants() });

export const useGetAssistant = (props: RoutingPathsType['assistant']) => {
    const { service } = useAssistantsService();
    return useQuery({
        queryKey: AdminQueryKeys.assistant(props),
        queryFn: () => service.getAssistant(props),
        staleTime: 0
    });
};

export const useCreateAssistant = () => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.createAssistant,
        onSuccess: async () => {
            const props = { queryKey: AdminQueryKeys.assistants() };
            await queryClientInstance?.invalidateQueries(props);
        }
    });
};

export const useUpdateAssistant = (props: RoutingPathsType['assistant']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.updateAssistant,
        onSuccess: async (data) => {
            await queryClientInstance?.setQueryData(AdminQueryKeys.assistant(props), data);
            refreshAssistants();
        }
    });
};

export const useRemoveAssistant = () => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.removeAssistant,
        onSuccess: async () => {
            refreshAssistants();
        }
    });
};


// #region versions

export const useGetAssistantVersions = (props: RoutingPathsType['assistant']) => {
    const { service } = useAssistantsService();
    return useQuery({
        queryKey: AdminQueryKeys.assistantVersions(props),
        queryFn: () => service.getAssistantVersions(props),
    });
};

export const useCreateAssistantVersion = (props: RoutingPathsType['assistant']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.createAssistantVersion,
        onSuccess: async () => {
            await queryClientInstance?.invalidateQueries({
                queryKey: AdminQueryKeys.assistantVersions(props)
            });
        }
    });
};

export const useGetAssistantVersion = (props: RoutingPathsType['versions']) => {
    const { service } = useAssistantsService();
    return useQuery({
        queryKey: AdminQueryKeys.assistantVersion(props),
        queryFn: () => service.getAssistantVersion(props),
    });
};

export const useUpdateAssistantVersion = (props: RoutingPathsType['versions']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.updateAssistantVersion,
        onSuccess: async (data) => {
            await queryClientInstance?.setQueryData(AdminQueryKeys.assistantVersion(props), data);
        }
    });
};
export const useRemoveAssistantVersion = (props: RoutingPathsType['assistant']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.removeAssistantVersion,
        onSuccess: async () => {
            await queryClientInstance?.invalidateQueries({
                queryKey: AdminQueryKeys.assistantVersions(props)
            });
        }
    });
};

export const useCreateAssistantVersionRevision = (props: RoutingPathsType["versions"]) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.createAssistantVersionDraft,
        onSuccess: async data => {
            await queryClientInstance?.setQueryData(AdminQueryKeys.assistantVersion(props), data);
        }
    });
};
export const useUpdateAssistantVersionRevision = (props: RoutingPathsType["versions"]) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.updateAssistantVersionRevision,
        onSuccess: async (data) => {
            await queryClientInstance?.setQueryData(AdminQueryKeys.assistantVersionRevision({ ...props, revisionKey: data.key }), data);
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistant(props) });
        }
    });
};
/**
 * @param revision_key '_' for latest revision
 */
export const useGetAssistantVersionRevision = (props: RoutingPathsType['versions'] & WithRevision) => {
    const { service } = useAssistantsService();
    return useQuery({
        queryKey: AdminQueryKeys.assistantVersionRevision(props),
        queryFn: () => service.getAssistantVersionRevision(props),
    });
};


export const usePublishLiveAssistantVersion = (props: PublishLiveAssistantVersionType) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: () => service.publishLiveAssistantVersion(props),
        onSuccess: async data => {
            await queryClientInstance?.setQueryData(AdminQueryKeys.assistantVersion(props), data);
            await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersions(props) });
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistant(props) });
        }
    });
};
export const usePublishBetaAssistantVersion = (props: RoutingPathsType['versions']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: () => service.publishBetaAssistantVersion(props),
        onSuccess: async data => {
            await queryClientInstance?.setQueryData(AdminQueryKeys.assistantVersion(props), data);
            await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersionRevision({ ...props, revisionKey: data.build.beta }) });
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistant(props) });
        }
    });
};


export const useUpdateAssistantVersionTools = (props: RoutingPathsType['versions']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.updateAssistantVersionTools,
        onSuccess: async data => {
            await queryClientInstance?.setQueryData(AdminQueryKeys.assistantVersionRevision({ ...props, revisionKey: data.key }), data);
        }
    });
};

export const useUpdateAssistantVersionDocuments = (props: RoutingPathsType['versions']) => {
    // const { service } = useAssistantsService();
    // return useMutation({
    //     mutationFn: service.updateAssistantVersionDocuments,
    //     onSuccess: async data => {
    // await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantVersion(params) });
    // await queryClientInstance?.setQueryData(AdminQueryKeys.assistantVersionRevision(props), data);
    // }
    // });
};

//#endregion



//#region tools

export const useUpdateAssistantAudience = (props: RoutingPathsType['share']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.updateAssistantAudience,
        onSuccess: async data => {
            await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistant(props) });
        }
    });
};

//#endregion


//#region evaluation

export const useCreateAssistantEvaluation = (props: RoutingPathsType['evaluate']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.createAssistantEvaluation,
        onSuccess: async data => {
            // TODO: check this out
            // await queryClientInstance?.setQueryData(AdminQueryKeys.assistant(props), data);
            await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantEvaluate(props) });
        }
    });
};

export const useCopyAssistantEvaluation = (props: RoutingPathsType['evaluate']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.copyAssistantEvaluation,
        onSuccess: async data => {
            await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantEvaluate(props) });
        }
    });
};

export const useUpdateAssistantEvaluation = (props: RoutingPathsType['evaluationCollection']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.updateAssistantEvaluation,
        onSuccess: async (_, variables) => {
            const key = AdminQueryKeys.assistantEvaluationCollection(props);
            await queryClientInstance?.setQueryData(key, (previous: CreateAssistantEvaluationResponse) => ({ ...previous, ...variables.body }));
        }
    });
};

export const useGetAssistantEvaluations = (props: RoutingPathsType['evaluate'] & { enabled?: boolean, refetchLatest?: boolean; }) => {
    const { service } = useAssistantsService();

    return useQuery({
        queryKey: AdminQueryKeys.assistantEvaluate(props),
        queryFn: () => service.getAssistantEvaluations(props),
        staleTime: props.refetchLatest === false ? Infinity : 0,
        enabled: props.enabled,
        initialData: [],
    });
};

export const useRemoveAssistantEvaluation = (props: RoutingPathsType['evaluate']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.removeAssistantEvaluation,
        onSuccess: async (_, variables) => {
            const key = AdminQueryKeys.assistantEvaluate(props);
            await queryClientInstance?.setQueryData(key, (previous: AssistantsEvaluationsResponse) => previous.filter(x => x.key !== variables.evaluationKey));// ({ ...previous, ...variables }));
        }
    });
};

export const useGetAssistantEvaluationCollection = (props: RoutingPathsType['evaluationCollection']) => {
    const { service } = useAssistantsService();
    return useQuery({
        queryKey: AdminQueryKeys.assistantEvaluationCollection(props),
        queryFn: () => service.getAssistantEvaluationCollection(props),
        // staleTime: 0,
    });
};



/// ------

export const useCreateAssistantEvaluationManualEntry = (props: RoutingPathsType['evaluationCollection']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: (entry: Omit<ManualEntry, keyof ResponseBaseData>) => service.createAssistantEvaluationManualEntry({ ...props, body: entry }),
        onSuccess: async (response) => {
            const key = AdminQueryKeys.assistantEvaluationCollection(props);
            await queryClientInstance?.setQueryData(key, (previous: AssistantsEvaluationCollectionResponse) => {
                const newData: AssistantsEvaluationCollectionResponse = {
                    ...previous,
                    manual_entries: [...previous.manual_entries, response],
                };
                return newData;
            });
        }
    });
};

export const useUpdateAssistantEvaluationManualEntry = (props: RoutingPathsType['evaluationCollection']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: (entry: Omit<ManualEntry, keyof ResponseBaseData> & { entryKey: string; }) => service.updateAssistantEvaluationManualEntry({ ...props, body: entry, entryKey: entry.entryKey, }),
        onSuccess: async (_, { entryKey, ...rest }) => {
            const key = AdminQueryKeys.assistantEvaluationCollection(props);

            await queryClientInstance?.setQueryData(key, (previous: AssistantsEvaluationCollectionResponse) => {
                const newData: AssistantsEvaluationCollectionResponse = {
                    ...previous,
                    manual_entries: previous.manual_entries.map(previous => {
                        if (previous.key === entryKey) {
                            return { ...previous, ...rest };
                        }
                        return previous;
                    }),
                };
                return newData;
            });
        }
    });
};

export const useRemoveAssistantEvaluationManualEntry = (props: RoutingPathsType['evaluationCollection']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: (entryKey: string) => service.deleteAssistantEvaluationManualEntry({ ...props, entryKey }),
        onSuccess: async (_, entryKey) => {
            const key = AdminQueryKeys.assistantEvaluationCollection(props);

            await queryClientInstance?.setQueryData(key, (previous: AssistantsEvaluationCollectionResponse) => {
                const newData: AssistantsEvaluationCollectionResponse = {
                    ...previous,
                    manual_entries: previous.manual_entries.filter(previous => previous.key !== entryKey),
                };
                return newData;
            });
        }
    });
};

export const useCreateCollectionEvaluationRun = (props: RoutingPathsType['evaluate']) => {
    const { service } = useAssistantsService();

    return useMutation({
        mutationFn: service.createCollectionEvaluationRun,
        onSuccess: async (_, entryKey) => {
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantEvaluationRuns(props) });
        }
    });
};


export const useDeleteCollectionAssistantEvaluationRun = (props: RoutingPathsType['evaluate']) => {
    const { service } = useAssistantsService();

    return useMutation({
        mutationFn: service.deleteCollectionAssistantEvaluationRun,
        onSuccess: async (_, entryKey) => {
            queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantEvaluationRuns(props) });
        }
    });
};

export const useGetCollectionAssistantEvaluationRun = (props: RoutingPathsType['evaluate'] & WithEvaluationRun) => {
    const { service } = useAssistantsService();
    const { assistantId, evaluationRunKey } = props;
    return useQuery({
        queryKey: AdminQueryKeys.assistantEvaluationRun(props),
        queryFn: () => !evaluationRunKey ? null : service.getCollectionAssistantEvaluationRun({ assistantId, evaluationRunKey }),
        staleTime: -1
    });
};

export const useGetCollectionAssistantEvaluationRuns = (props: RoutingPathsType['evaluate'] & WithRevision & WithVersion & { enabled?: boolean; refetchLatest?: boolean; }) => {
    const { service } = useAssistantsService();

    return useQuery({
        queryKey: AdminQueryKeys.assistantEvaluationRuns(props),
        queryFn: () => service.getCollectionAssistantEvaluationRuns(props),
        enabled: props.enabled,
        staleTime: !!props.refetchLatest ? 0 : Infinity,
    });
};

//#endregion

export const useExportAssistantEvaluationEntriesCsv = () => {
    const { service } = useAssistantsService();
    return useMutation<boolean, Error, ExportAssistantEvaluationEntriesCsvType>({
        mutationFn: (params: ExportAssistantEvaluationEntriesCsvType) => service.exportAssistantEvaluationEntriesCsv(params)
    });
};

export const useImportAssistantEvaluationEntriesCsv = (props: RoutingPathsType['evaluationCollection']) => {
    const { service } = useAssistantsService();
    return useMutation({
        mutationFn: service.importAssistantEvaluationEntriesCsv,
        onSuccess: async () => {
            await queryClientInstance?.invalidateQueries({ queryKey: AdminQueryKeys.assistantEvaluationCollection(props) });
        }
    });
};
